import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, Bell, Clock } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { usePerformanceHubQuery } from "@/modules/performance-tracking/presentation/api/queries";
import { performanceRoutes } from "@/modules/performance-tracking/presentation/performanceRoutes";
import { useTrainingHubQuery } from "@/modules/training-standard/presentation/api/queries";
import { trainingRoutes } from "@/modules/training-standard/presentation/trainingRoutes";
import { formatDate, formatDateLocal } from "@/shared/lib/date-time";

const CATEGORY_LABEL = {
  checklist: "ລາຍການກວດສອບ",
  task: "ວຽກມອບໝາຍ",
} as const;

type PortalAlertRow =
  | {
      key: string;
      category: "checklist";
      checklistId: string;
      title: string;
      alertStatus: "overdue" | "due_soon";
      sortDueMs: number;
      submissionDueAt: string | null;
    }
  | {
      key: string;
      category: "task";
      title: string;
      alertStatus: "overdue" | "due_soon";
      sortDueMs: number;
      dueDate: string | null;
      source: "meeting" | "project";
      meetingId?: string;
      projectId?: string;
    };

function buildUnifiedRows(
  checklist: Array<{
    id: string;
    title: string;
    alertStatus: "overdue" | "due_soon";
    submissionDueAt: string | null;
  }>,
  tasks: Array<{
    id: string;
    source: "meeting" | "project";
    title: string;
    alertStatus: "overdue" | "due_soon";
    dueDate: string | null;
    meetingId?: string;
    projectId?: string;
  }>,
): PortalAlertRow[] {
  const rows: PortalAlertRow[] = [];
  for (const n of checklist) {
    const sortDueMs = n.submissionDueAt
      ? Date.parse(n.submissionDueAt)
      : Number.POSITIVE_INFINITY;
    rows.push({
      key: `checklist-${n.id}`,
      category: "checklist",
      checklistId: n.id,
      title: n.title,
      alertStatus: n.alertStatus,
      sortDueMs: Number.isFinite(sortDueMs) ? sortDueMs : Number.POSITIVE_INFINITY,
      submissionDueAt: n.submissionDueAt,
    });
  }
  for (const n of tasks) {
    const sortDueMs = n.dueDate ? Date.parse(n.dueDate) : Number.POSITIVE_INFINITY;
    rows.push({
      key: n.id,
      category: "task",
      title: n.title,
      alertStatus: n.alertStatus,
      sortDueMs: Number.isFinite(sortDueMs) ? sortDueMs : Number.POSITIVE_INFINITY,
      dueDate: n.dueDate,
      source: n.source,
      meetingId: n.meetingId,
      projectId: n.projectId,
    });
  }
  rows.sort((a, b) => {
    const ra = a.alertStatus === "overdue" ? 0 : 1;
    const rb = b.alertStatus === "overdue" ? 0 : 1;
    if (ra !== rb) return ra - rb;
    return a.sortDueMs - b.sortDueMs;
  });
  return rows;
}

function RowLink({
  row,
  children,
}: {
  row: PortalAlertRow;
  children: ReactNode;
}) {
  const className =
    "flex flex-col items-start gap-1.5 py-2.5 text-left [&:hover]:no-underline";
  if (row.category === "checklist") {
    return (
      <Link
        to={trainingRoutes.checklistsList}
        search={{ checklistId: row.checklistId }}
        className={className}
      >
        {children}
      </Link>
    );
  }
  if (row.source === "meeting" && row.meetingId) {
    return (
      <Link
        to="/app/communication/minutes/$id"
        params={{ id: row.meetingId }}
        search={{ from: "minutes" }}
        className={className}
      >
        {children}
      </Link>
    );
  }
  if (row.source === "project" && row.projectId) {
    return (
      <Link
        to="/app/performance/projects/$projectId"
        params={{ projectId: row.projectId }}
        className={className}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link to={performanceRoutes.tasks} className={className}>
      {children}
    </Link>
  );
}

function RowMeta({ row }: { row: PortalAlertRow }) {
  return (
    <span className="flex flex-wrap items-center gap-2 text-muted-foreground text-xs">
      {row.alertStatus === "overdue" ? (
        <span className="inline-flex items-center gap-1 font-medium text-destructive">
          <AlertTriangle className="size-3.5 shrink-0" />
          ເກີນກຳນົດ
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 font-medium text-amber-800 dark:text-amber-200">
          <Clock className="size-3.5 shrink-0" />
          ໃກ້ຄົບກຳນົດ
        </span>
      )}
      {row.category === "checklist" && row.submissionDueAt ? (
        <span>ກຳນົດສົ່ງ {formatDate(row.submissionDueAt)}</span>
      ) : null}
      {row.category === "task" && row.dueDate ? (
        <span>ກຳນົດ {formatDateLocal(row.dueDate)}</span>
      ) : null}
    </span>
  );
}

/** ກະດິ່ງດຽວ: ລາຍການກວດສອບ + ວຽກມອບໝາຍ — ສະແດງປະເພດແຈ້ງເຕືອນຕໍ່ແຖວ */
export function HeaderPortalNotificationsBell() {
  const { isAuthenticated } = useAuthState();
  const training = useTrainingHubQuery({ enabled: isAuthenticated });
  const performance = usePerformanceHubQuery({ enabled: isAuthenticated });

  const checklistItems = training.data?.checklistNotifications ?? [];
  const taskItems = performance.data?.taskNotifications ?? [];

  const unified = useMemo(
    () => buildUnifiedRows(checklistItems, taskItems),
    [checklistItems, taskItems],
  );

  const count = unified.length;
  const hasOverdue = unified.some((r) => r.alertStatus === "overdue");

  const showBlockingLoading =
    isAuthenticated &&
    unified.length === 0 &&
    (training.isPending || performance.isPending);

  const showError =
    isAuthenticated &&
    unified.length === 0 &&
    !showBlockingLoading &&
    training.isError &&
    performance.isError;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="relative shrink-0"
          aria-label="ການແຈ້ງເຕືອນ (ກວດສອບ ແລະ ວຽກມອບໝາຍ)"
        >
          <Bell className="size-4" />
          {!showBlockingLoading && !showError && count > 0 ? (
            <span
              className={
                hasOverdue
                  ? "absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 font-semibold text-[10px] text-destructive-foreground"
                  : "absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 font-semibold text-[10px] text-amber-950"
              }
            >
              {count > 9 ? "9+" : count}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-80">
        <DropdownMenuLabel className="font-semibold">
          ການແຈ້ງເຕືອນ
        </DropdownMenuLabel>
        <p className="px-2 pb-1 text-muted-foreground text-xs">
          ປະເພດ: ກວດສອບ · ວຽກມອບໝາຍ
        </p>
        <DropdownMenuSeparator />
        {showBlockingLoading ? (
          <p className="px-2 py-3 text-muted-foreground text-sm">ກຳລັງໂຫຼດ…</p>
        ) : showError ? (
          <p className="px-2 py-3 text-muted-foreground text-sm">ໂຫຼດບໍ່ສຳເລັດ</p>
        ) : count === 0 ? (
          <p className="px-2 py-3 text-muted-foreground text-sm">
            ບໍ່ມີການແຈ້ງເຕືອນຊ່ວງນີ້
          </p>
        ) : (
          <div className="max-h-72 overflow-y-auto">
            {unified.map((row) => (
              <DropdownMenuItem key={row.key} asChild className="cursor-pointer">
                <RowLink row={row}>
                  <Badge variant="secondary" className="font-normal text-[10px]">
                    {CATEGORY_LABEL[row.category]}
                  </Badge>
                  <span className="line-clamp-2 w-full font-medium leading-snug">
                    {row.title}
                  </span>
                  <RowMeta row={row} />
                </RowLink>
              </DropdownMenuItem>
            ))}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link
            to={trainingRoutes.checklistsList}
            search={{
              checklistId: undefined,
              sortBy: undefined,
              sortOrder: undefined,
            }}
          >
            ເບິ່ງລາຍການກວດສອບ
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to={performanceRoutes.tasks}>ເບິ່ງວຽກມອບໝາຍ</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
